import { ViewportScroller } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { EventService, LoginEvent } from '@spartacus/core';
import { BehaviorSubject, Observable, Subscription, combineLatest, interval, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  AreaOfUseService,
  ArticleService,
  CatalogKeysService,
  CategoryLevelType,
  CategoryService,
  PriceCampaignService,
  ProductService,
} from '../../../../core/catalog';
import {
  AREA_OF_USE_ALL_CATEGORIES_CODE,
  Article,
  Category,
  EntityMap,
  Product,
  SapPriceCampaignDetails,
  SolrResultEntityRef,
  SolrSearchResult,
  SubstituteRef,
} from '../../../../core/model';
import { LanguageSetEvent } from '../../../../core/site-context';
import { PrincipalConfigurationService, UserCatalogTabTypeEvent } from '../../../../core/user';
import { shallowEqualObjects } from '../../../../core/util';
import { ItemListType } from '../../../tracking/events/tracking-events';
import { CatalogSearchService, CatalogTabTypes, SortPrefix } from './catalog-search.service';

@Component({
  selector: 'py-catalog',
  templateUrl: './catalog.component.html',
  styleUrls: ['./catalog.component.scss'],
  host: {
    ngSkipHydration: 'true',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CatalogComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() popularProducts: Array<any> = [];
  products$: Observable<Observable<Product>[]>;
  articles$: Observable<Observable<Article>[]>;
  category$: Observable<Category>;
  searchCategories$: Observable<Category[]>;
  searchResults$: Observable<SolrSearchResult>;
  listItemQueryParams$: Observable<EntityMap<string>>;
  catalogKeysChanged$: Observable<boolean>;
  enableTogglingOfCustomerAssortment$: Observable<boolean>;
  catalogProductItemSecondary$: Observable<boolean>;

  searchResultsFromService$: Observable<SolrSearchResult> = this.catalogSearchService.results$;
  productHits$: Observable<number> = this.catalogSearchService.productHits$;
  articleHits$: Observable<number> = this.catalogSearchService.articleHits$;
  loadingProducts$: Observable<boolean> = this.catalogSearchService.loading$;
  productsLoaded$: Observable<boolean> = this.catalogSearchService.loaded$;
  loadingArticles$: Observable<boolean> = this.catalogSearchService.loading$;
  articlesLoaded$: Observable<boolean> = this.catalogSearchService.loaded$;

  private _activeTabIndex$ = new BehaviorSubject<CatalogTabTypes>(undefined);
  activeTabIndex$ = combineLatest([this._activeTabIndex$, this.activatedRoute.queryParamMap]).pipe(
    map(([activeTabIndex, paramMap]) => activeTabIndex ?? (paramMap.get('tab') as CatalogTabTypes)),
    distinctUntilChanged()
  );
  disableProductTab$: Observable<boolean>;
  hasAreaOfUse$: Observable<boolean>;

  tabTypes = CatalogTabTypes;

  // Discontinued Articles
  discontinuedArticle: Article;
  hasDiscontinuedArticle: boolean = false;
  articleResultRefs$: Observable<SolrResultEntityRef[]>;
  substituteRefs: SubstituteRef[] = [];
  substituteProducts: Observable<Product>[];
  subsituteArticlesCodes: string[];
  freeTextSearch: string;

  // Price Campaign
  hasPriceCampaign: boolean = false;
  numberOfSelectedCampaigns: number = 0;

  priceCampaignDetails: SapPriceCampaignDetails;

  private subscription = new Subscription();

  catalogUsageKey$: Observable<string>;
  ltemListTypesubs = ItemListType.SUBSTITUTES_ARTICLES;
  ltemListTypeCatalog = ItemListType.CATALOG;

  constructor(
    private catalogKeysService: CatalogKeysService,
    private productService: ProductService,
    private articleService: ArticleService,
    private categoryService: CategoryService,
    private catalogSearchService: CatalogSearchService,
    private priceCampaignService: PriceCampaignService,
    protected activatedRoute: ActivatedRoute,
    private principalConfigurationService: PrincipalConfigurationService,
    private router: Router,
    private viewportScroller: ViewportScroller,
    private areaOfUseService: AreaOfUseService,
    private cd: ChangeDetectorRef,
    private eventService: EventService
  ) {}

  ngAfterViewInit(): void {
    this.setInitialScrollPosition();
  }

  ngOnInit(): void {
    this.subscription.add(
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationStart && event.navigationTrigger === 'imperative'),
          map((event) => event as NavigationStart)
        )
        .subscribe((_event) => {
          this.catalogSearchService.setPreviousScrollPosition(this.viewportScroller.getScrollPosition()[1]);
        })
    );

    this.subscription.add(this.catalogSearchService.searchByRouting$.subscribe());

    this.catalogKeysChanged$ = this.catalogKeysService.getKeysChanged().pipe(startWith(false));
    this.enableTogglingOfCustomerAssortment$ = this.principalConfigurationService.isEnabled('enableTogglingOfCustomerAssortment');

    const paramMya$ = this.activatedRoute.queryParamMap.pipe(
      map((p) => p.get('mya')),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const paramAou$ = this.activatedRoute.queryParamMap.pipe(
      map((p) => p.get('aou')),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const paramQuery$ = this.activatedRoute.queryParamMap.pipe(
      map((p) => p.get('query')),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.subscription.add(paramQuery$.subscribe((newQuery) => this.handleRobotsMetaTag(newQuery)));

    this.disableProductTab$ = this.activatedRoute.queryParamMap.pipe(
      map((p) => (p.get('query') || '').includes('productCode:CUST_UNIQ'))
    );

    this.catalogUsageKey$ = combineLatest([paramMya$, this.catalogKeysChanged$, this.enableTogglingOfCustomerAssortment$]).pipe(
      distinctUntilChanged(),
      switchMap(([paramMya, _, enableTogglingOfCustomerAssortment]) => {
        const key = enableTogglingOfCustomerAssortment ? 'mya:' + ((paramMya ?? '0') === '1') : 'default';
        return of(key);
      })
    );

    this.listItemQueryParams$ = combineLatest([paramMya$, paramAou$]).pipe(
      map(([myaValue, aouValue]) => {
        const params = {};
        if (!!myaValue) {
          params['mya'] = myaValue;
        }
        if (!!aouValue) {
          params['aou'] = aouValue;
        }

        return params;
      })
    );

    this.category$ = combineLatest([this.activatedRoute.data, this.activatedRoute.queryParamMap]).pipe(
      switchMap(([data, queryParams]) => {
        if (queryParams.get('aou') && queryParams.get('aou') !== AREA_OF_USE_ALL_CATEGORIES_CODE) {
          return this.areaOfUseService.getAreaOfUse(queryParams.get('aou'));
        }

        if (data?.categoryCode) {
          return this.catalogKeysService
            .getCatalogUsageKeyForParams(queryParams.get('mya'))
            .pipe(switchMap((catalogUsageKey) => this.categoryService.getCategory(data?.categoryCode, catalogUsageKey)));
        }

        return of(undefined);
      }),
      tap(() => this.cd.detectChanges()),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.searchResults$ = combineLatest([
      this.activeTabIndex$.pipe(distinctUntilChanged()),
      this.searchResultsFromService$.pipe(distinctUntilChanged(shallowEqualObjects)),
    ]).pipe(
      filter(([activeTab, result]) =>
        activeTab === CatalogTabTypes.Articles ? !!result.articleResultRefs : !!result.productResultRefs
      ),
      map(([_activeTab, result]) => result),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.products$ = this.searchResults$.pipe(
      switchMap((searchResult) => {
        return this.productService.getProducts(
          searchResult.productResultRefs?.map((productResultRef) => productResultRef.ref) || []
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.subscription.add(
      this.searchResults$.subscribe((searchResult) => {
        this.articleService.getArticles(searchResult.articleResultRefs?.map((articleResultRef) => articleResultRef.ref) || []);
      })
    );

    this.searchCategories$ = this.searchResults$.pipe(
      distinctUntilChanged(shallowEqualObjects),
      switchMap((searchResults) => {
        const aouCode = this.activatedRoute.snapshot.queryParams['aou'];
        const query = this.activatedRoute.snapshot.queryParams['query'];

        if (aouCode) {
          if (aouCode === AREA_OF_USE_ALL_CATEGORIES_CODE && !query) {
            return this.areaOfUseService.getAreasOfUse();
          } else if (aouCode !== AREA_OF_USE_ALL_CATEGORIES_CODE) {
            return this.areaOfUseService
              .getAreaOfUse(aouCode)
              .pipe(
                switchMap((areaOfUse) =>
                  this.areaOfUseService.getAreasOfUseByRefsAndSuperCategoryRef(areaOfUse.code, searchResults.areaOfUseRefs)
                )
              );
          }
          return this.areaOfUseService.getAreasOfUseByRefsAndLevel(0, searchResults.areaOfUseRefs);
        } else {
          const categoryCode = this.activatedRoute.snapshot.data.categoryCode;
          const noBreadcrumbsAndNoFreeTextSearch = searchResults?.breadcrumbs?.length === 0 && !searchResults.freeTextSearch;
          if (!categoryCode && (!query || noBreadcrumbsAndNoFreeTextSearch)) {
            return this.catalogKeysService
              .getCatalogUsageKey()
              .pipe(
                switchMap((catalogUsageKey) => this.categoryService.getCategoriesByLevel(CategoryLevelType.ROOT, catalogUsageKey))
              );
          } else if (categoryCode) {
            const hasAllCategories = searchResults?.breadcrumbs?.some((breadcrumb) => breadcrumb.facetCode === 'allCategories');
            const shouldUseSelectedCategoryChildren: boolean =
              !searchResults?.freeTextSearch && searchResults?.breadcrumbs?.length === 1 && hasAllCategories;

            return combineLatest([
              this.catalogKeysService.getCatalogUsageKey(),
              this.categoryService.getCategory(categoryCode).pipe(filter((category) => !!category)),
            ]).pipe(
              switchMap(([catalogUsageKey, category]) => {
                const categoryRefs = shouldUseSelectedCategoryChildren
                  ? category.subCategories
                      .filter((subCategory) => subCategory.level === category.level + 1)
                      .map((subCategory) => subCategory.code)
                  : searchResults?.categoryRefs;
                return this.categoryService.getCategoriesByRefsAndLevel(category.level + 1, categoryRefs, catalogUsageKey);
              })
            );
          }

          return this.catalogKeysService
            .getCatalogUsageKey()
            .pipe(
              switchMap((catalogUsageKey) =>
                this.categoryService.getCategoriesByRefsAndLevel(0, searchResults?.categoryRefs, catalogUsageKey)
              )
            );
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.articleResultRefs$ = this.searchResults$.pipe(
      tap((searchResults) => (this.freeTextSearch = searchResults?.freeTextSearch)),
      map((searchResults) => {
        if (searchResults?.articleResultRefs?.length) {
          return searchResults?.articleResultRefs;
        }
        return [];
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.subscription.add(
      this.articleResultRefs$
        .pipe(
          tap((_) => this.resetDiscontinuedArticleData()),
          map((articleResultRefs) => {
            return articleResultRefs?.length === 1
              ? articleResultRefs?.find((articleResultRef) => articleResultRef.ref === this.freeTextSearch)
              : undefined;
          }),
          switchMap((articleResultRef) => this.articleService.getArticle(articleResultRef?.ref)),
          switchMap((article) => {
            if (!!article && this.articleService.isArticleDiscontinued(article)) {
              this.hasDiscontinuedArticle = true;
              this.discontinuedArticle = article;
              return this.articleService.getSubstitutesArticles(article.code);
            }
            return of([]);
          })
        )
        .subscribe((substitutes: SubstituteRef[]) => {
          this.substituteRefs = substitutes ? substitutes : [];
          this.substituteProducts = this.getProductsFromSubstitutes(this.substituteRefs);
          this.subsituteArticlesCodes = this.getArticlesCodesFromSubstitutes(this.substituteRefs);
          this.cd.detectChanges();
        })
    );

    this.hasAreaOfUse$ = this.activatedRoute.queryParams.pipe(
      map((params) => (params['aou'] ? true : false)),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.subscription.add(
      this.activatedRoute.queryParamMap
        .pipe(
          tap((_) => {
            this.hasPriceCampaign = false;
            this.numberOfSelectedCampaigns = 0;
          }),
          map((params) => params.get('query')),
          map((query) => query?.match(/sapPriceCampaign:[^:&]*/g)),
          filter((matcher) => !!matcher && matcher.length > 0),
          tap((matcher) => {
            this.hasPriceCampaign = true;
            this.numberOfSelectedCampaigns = matcher.length;
          }),
          map((matcher) => matcher[0]),
          map((campaignParam) => campaignParam.split(':')[1]),
          switchMap((campaignCode) => this.priceCampaignService.getPriceCampaignDetails(campaignCode))
        )
        .subscribe((campaignDetails) => (this.priceCampaignDetails = campaignDetails))
    );

    this.catalogProductItemSecondary$ = this.principalConfigurationService
      .isEnabled('catalogProductItemSecondary')
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    // Catalog component needs to be reload with current query when site language was changed.
    // It's to make sure fresh search results will be received from backend and displayed.
    this.subscription.add(
      this.eventService.get(LanguageSetEvent).subscribe((_) => {
        this.router.navigate([], {
          queryParams: { query: this.activatedRoute.snapshot.queryParams.query },
          queryParamsHandling: 'merge',
          relativeTo: this.activatedRoute,
        });
      })
    );

    // Catalog component needs to be reload with current query when user was logged in because search state is cleared when the logout action has been performed
    this.subscription.add(
      this.eventService.get(LoginEvent).subscribe((_) => {
        this.router.navigate([], {
          queryParams: { query: this.activatedRoute.snapshot.queryParams.query },
          queryParamsHandling: 'merge',
          relativeTo: this.activatedRoute,
        });
      })
    );

    this.subscription.add(
      this.activeTabIndex$.subscribe((tab: CatalogTabTypes) => {
        this.loadTab(tab);
      })
    );

    //Set the active tab
    this.subscription.add(
      combineLatest([
        this.activatedRoute.queryParamMap.pipe(distinctUntilChanged()),
        this.productHits$.pipe(distinctUntilChanged()),
        this.articleHits$.pipe(distinctUntilChanged()),
      ])
        .pipe(
          filter(([, productHits, articleHits]) => productHits >= 0 || articleHits >= 0),
          map(([params, productHits, articleHits]) => {
            const paramTab = params.get('tab') ? (params.get('tab') as CatalogTabTypes) : CatalogTabTypes.Products;

            if (typeof productHits === 'undefined' || typeof articleHits === 'undefined') {
              return;
            }

            if (paramTab === CatalogTabTypes.Products && productHits === 0 && articleHits > 0) {
              return CatalogTabTypes.Articles;
            }
            if (paramTab === CatalogTabTypes.Articles && articleHits === 0 && productHits > 0) {
              return CatalogTabTypes.Products;
            }
            return paramTab === CatalogTabTypes.Products && productHits > 0 ? CatalogTabTypes.Products : CatalogTabTypes.Articles;
          }),
          distinctUntilChanged()
        )
        .subscribe((activeTabIndex) => {
          if (!!activeTabIndex) {
            this._activeTabIndex$.next(activeTabIndex);
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  nextPage($event: number, totalPages?: number) {
    if (totalPages === undefined || $event < totalPages) {
      this.catalogSearchService.loadMore($event);
    }
  }

  tabSelected(tabIndex: CatalogTabTypes) {
    this._activeTabIndex$.next(tabIndex);
  }

  loadTab(tabIndex: CatalogTabTypes) {
    this.searchResults$
      .pipe(
        take(1),
        map((searchResults) => searchResults?.sorts || []),
        withLatestFrom(this.activatedRoute.queryParamMap)
      )
      .subscribe(([sorts, params]) => {
        const activePrefix = tabIndex === CatalogTabTypes.Products ? SortPrefix.Article : SortPrefix.Product;
        const newPrefix = tabIndex === CatalogTabTypes.Products ? SortPrefix.Product : SortPrefix.Article;
        const defaultSort = params.get('sort') === null;
        let sortCode = sorts.find((s) => s.selected)?.code || null;

        if (defaultSort) {
          // keep using default sort after changing tabs
          sortCode = null;
        } else if (sortCode.toLowerCase().substring(0, 2) === activePrefix) {
          sortCode =
            sorts.find((s) => s.code.toLowerCase() === `${newPrefix}${sortCode.toLowerCase().substring(2)}`)?.code || null;
        }

        this.catalogSearchService.setSearchType(tabIndex, sortCode);

        this.eventService.dispatch(new UserCatalogTabTypeEvent([tabIndex]));
      });
  }

  filter($event: string) {
    this.catalogSearchService.setQuery($event);
  }

  getArticlesCodesFromSubstitutes(substituteRefs: SubstituteRef[]): string[] {
    return this.articleService.getArticlesCodesFromSubstitutes(substituteRefs);
  }

  getProductsFromSubstitutes(substituteRefs: SubstituteRef[]): Observable<Product>[] {
    return this.articleService
      .getProductsCodesFromSubstitutes(substituteRefs)
      .map((code) => this.productService.getProduct(code));
  }

  private setInitialScrollPosition(): void {
    if (this.activatedRoute.snapshot.data?.restoreScrollPosition) {
      // Set previous position when we back from article page (navigation has changed)
      this.scrollToPreviousPosition();
    } else {
      // Set postion as top of the page
      this.viewportScroller.scrollToPosition([0, 0]);
    }
  }

  private handleRobotsMetaTag(query: string): void {
    //when we delete query it remains :: so we have to remove that characters in order to check if query exists
    const parsedNewQuery = query?.replace(/:/g, '');
    this.catalogSearchService.updateRobotsMetaTag(parsedNewQuery);
  }

  private scrollToPreviousPosition(): void {
    const previousScrollPosition = this.catalogSearchService.previousScrollPosition$.value;
    this.catalogSearchService.wasScrolledToPreviousPosition$?.next(true);

    if (previousScrollPosition) {
      this.viewportScroller.scrollToPosition([0, previousScrollPosition]);
      // Handling case when scroll position is still wrong, because the page elements are not fully rendered yet and document is not high enough.
      // In this case scrolling is retried in intervals until the wanted position is achieved or number of tries is exceeded.
      if (previousScrollPosition !== this.viewportScroller.getScrollPosition()[1]) {
        this.subscription.add(
          interval(100)
            .pipe(takeWhile((count) => previousScrollPosition !== this.viewportScroller.getScrollPosition()[1] && count < 5))
            .subscribe(() => {
              this.catalogSearchService.wasScrolledToPreviousPosition$?.next(true);
              this.viewportScroller.scrollToPosition([0, previousScrollPosition]);
            })
        );
      }
    }
  }

  private resetDiscontinuedArticleData(): void {
    this.hasDiscontinuedArticle = false;
    this.discontinuedArticle = undefined;
    this.substituteRefs = [];
  }
}
